<template>
  <div class="inline-flex">
    <slot :show="show" :set-show="setShow">
      <BaseButton small primary @click="setShow(true)"> Edit Offer </BaseButton>
    </slot>
    <Portal to="app-dialog" v-if="show && !loading">
      <VModal ref="modal" @modal:hidden="setShow(false)" disable-backdrop-click>
        <div class="w-full max-w-6xl mx-auto">
          <div class="mb-4">
            <div class="flex items-center justify-end">
              <div class="flex-none" @click.stop>
                <button
                  class="h-10 w-10 inline-flex items-center justify-center bg-white hover:bg-gray-200 focus:outline-none rounded-full"
                  @click="$refs.modal.close()"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" class="inline-flex w-5 h-5">
                    <path
                      d="M12 10.586l4.95-4.95 1.414 1.414-4.95
                      4.95 4.95 4.95-1.414 1.414-4.95-4.95-4.95
                      4.95-1.414-1.414 4.95-4.95-4.95-4.95L7.05
                      5.636z"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div class="rounded shadow-xs w-full max-w-6xl mx-auto" @click.stop>
            <div class="rounded shadow-lg bg-white overflow-hidden divide-y divide-gray-200">
              <div class="bg-white px-4 sm:px-6 py-5">
                <div class="flex items-center justify-between">
                  <div class="flex-1">
                    <h1 class="text-sm">Edit Offer</h1>
                  </div>
                </div>
              </div>
              <template>
                <div class="bg-gray-200">
                  <BaseVeeForm :action="submitForm" v-slot="{ isPending, invalid }">
                    <TabbedNavigation>
                      <template #general>
                        <General
                          :issue-date="formData.issueDate"
                          :number="formData.number"
                          :assigned-user-id="formData.assignedUserId"
                          :account-id="account.id"
                          can-edit
                          @update:issue-date="formData.issueDate = $event"
                          @update:assigned-user-id="formData.assignedUserId = $event"
                        />
                        <CreateOrder v-if="isAgency" :offer-id="offerId" />
                        <TransferOffer
                          v-if="canTransferOffer"
                          :account-id="account.id"
                          :offer-id="offerId"
                          @offer-transferred="fetchOffer"
                        />
                      </template>

                      <template #issuer>
                        <Issuer
                          :account-id="formData.accountUuid"
                          :issuer-name="formData.issuerName"
                          :issuer-address="formData.issuerAddress"
                          :issuer-vat-number="formData.issuerVatNumber"
                          :issuer-vat-office="formData.issuerVatOffice"
                          :can-change-account="canChangeIssuer"
                          :can-update-account="canUpdateIssuer"
                          @update:issuer-name="formData.issuerName = $event"
                          @update:issuer-address="formData.issuerAddress = $event"
                          @update:issuer-vat-number="formData.issuerVatNumber = $event"
                          @update:issuer-vat-office="formData.issuerVatOffice = $event"
                        />
                      </template>

                      <template #recipient>
                        <Recipient
                          :account-relation-id="formData.accountRelationUuid"
                          :contact-id="formData.contactId"
                          :recipient-name="formData.recipientName"
                          :recipient-address="formData.recipientAddress"
                          :recipient-vat-number="formData.recipientVatNumber"
                          :recipient-vat-office="formData.recipientVatOffice"
                          :can-create-account="isAgency"
                          :can-change-recipient="canChangeRecipient"
                          :can-update-recipient="canUpdateRecipient"
                          @update:recipient-name="formData.recipientName = $event"
                          @update:recipient-address="formData.recipientAddress = $event"
                          @update:recipient-vat-number="formData.recipientVatNumber = $event"
                          @update:recipient-vat-office="formData.recipientVatOffice = $event"
                          @update:account-relation-id="formData.accountRelationUuid = $event"
                        />
                      </template>

                      <template #details>
                        <Lines
                          :default-vat-rate="defaultVatRate"
                          :can-edit-lines="canEditLines"
                          :offer-lines="formData.offerLines"
                          @change="updateOfferLines"
                        />
                      </template>

                      <template #preview>
                        <div class="flex flex-col items-center space-y-4">
                          <BaseButton
                            v-if="offer.uuid"
                            tag="router-link"
                            small
                            primary
                            :to="{
                              name: 'offers.offerId.preview',
                              params: { offerId: offer.uuid },
                            }"
                            target="_blank"
                          >
                            Open in another tab
                          </BaseButton>
                          <div
                            class="bg-white rounded-lg border border-gray-400 overflow-hidden pb-16"
                          >
                            <PrintableOffer :offer="formData" :account-relation="accountRelation" />
                          </div>
                        </div>
                      </template>
                    </TabbedNavigation>
                    <div class="space-y-6 p-4 sm:p-6 bg-white border-t">
                      <div class="flex space-x-2 sticky bottom-0">
                        <BaseButton primary :disabled="isPending || invalid || !hasOfferLines">
                          Update offer
                        </BaseButton>
                      </div>
                    </div>
                  </BaseVeeForm>
                </div>
              </template>
            </div>
          </div>
        </div>
      </VModal>
    </Portal>
  </div>
</template>

<script>
import moment from 'moment';
import PrintableOffer from 'Components/Offers/PrintableOffer.vue';
import TabbedNavigation from './TabbedNavigation.vue';
import General from './General.vue';
import Issuer from './Issuer.vue';
import Recipient from './Recipient.vue';
import Lines from './Lines.vue';
import CreateOrder from './CreateOrder.vue';
import TransferOffer from './TransferOffer.vue';

export default {
  components: {
    TabbedNavigation,
    General,
    Issuer,
    Recipient,
    Lines,
    CreateOrder,
    PrintableOffer,
    TransferOffer,
  },
  props: {
    offerId: {
      type: String,
      required: true,
    },
    canChangeIssuer: Boolean,
    canUpdateIssuer: Boolean,
    canChangeRecipient: Boolean,
    canUpdateRecipient: Boolean,
    canEditLines: Boolean,
  },
  data() {
    return {
      show: false,
      loading: false,
      formData: {},
    };
  },
  computed: {
    offer() {
      return this.$store.state.entities.offers.byId[this.offerId];
    },
    account() {
      return this.$store.state.entities.accounts.byId[this.offer.accountUuid];
    },
    accountRelation() {
      return (
        this.$store.state.entities.accountRelations.byId[this.formData.accountRelationUuid] || null
      );
    },
    defaultVatRate() {
      return this.accountRelation?.defaultVatRate || 0;
    },
    isAgency() {
      return this.account?.type === 'organization';
    },
    hasOfferLines() {
      return (this.formData?.offerLines || []).length > 0;
    },
    hasOrder() {
      return typeof this.$store.state.ui?.[`offer:${this.offerId}:order`]?.orderId === 'string';
    },
    canTransferOffer() {
      return typeof this.$route.params.accountId === 'undefined' && !this.hasOrder;
    },
  },
  watch: {
    show(value) {
      if (!value) {
        this.setFormData();
      } else {
        this.fetchOffer();
      }
    },
    'offer.accountUuid': {
      handler(accountId, oldAccountId) {
        if (accountId === oldAccountId) {
          return;
        }

        if (this.$route.params.accountId === undefined) {
          return;
        }

        this.$router.replace({
          name: this.$route.name,
          params: {
            ...this.$route.params,
            accountId,
          },
        });
      },
    },
  },
  methods: {
    setLoading(value) {
      this.loading = Boolean(value);
    },
    async fetchOffer() {
      try {
        this.setLoading(true);
        await this.$store.dispatch({
          type: 'ui/offers/read',
          offerUuid: this.offerId,
          params: {
            include: [
              'account.paymentAccounts',
              'account.logo',
              'contact',
              'accountRelation.relatedAccount.logo',
              'accountRelation.relatedAccount.registrations',
              'lines.product',
            ].join(','),
          },
        });
        this.setFormData();
      } catch (error) {
        this.setShow(false);
      } finally {
        this.setLoading(false);
      }
    },
    setShow(value) {
      this.show = value;
    },
    setFormData() {
      this.formData = {
        number: '',
        issueDate: moment().format('YYYY-MM-DD'),
        issuerName: '',
        issuerAddress: '',
        issuerVatNumber: '',
        issuerVatOffice: '',
        recipientName: '',
        recipientAddress: '',
        recipientVatNumber: '',
        recipientVatOffice: '',
        contactId: null,
        assignedUserId: null,
        accountRelationUuid: null,
        ...(this.offer || {}),
        offerLines: (this?.offer?.offerLines || [])
          .map((offerLineId) => {
            if (typeof offerLineId === 'string') {
              const offerLine = this.$store.state.entities.offerLines.byId[offerLineId];

              return {
                ...offerLine,
              };
            }

            return offerLineId;
          })
          .filter((o) => o),
      };
    },
    updateOfferLines(offerLines) {
      this.formData.offerLines = offerLines;
    },
    async submitForm() {
      try {
        await this.$store.dispatch({
          type: 'ui/offers/update',
          offerId: this.offerId,
          formData: this.formData,
        });

        this.$emit('success');

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
  },
};
</script>
